import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/react-hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import accountIcon from "@iconify/icons-mdi/account-network";
import keyIcon from "@iconify/icons-mdi/key";
import { InlineIcon } from "@iconify/react";
import Validator from "fastest-validator";
import {
  MyHrForgotPasswordDialogSSO,
  MyHrHelpdeskContact,
} from "my-hr/components";
import { SSO_LOGIN } from "my-hr/graphql";
import { OcLoading } from "oc/components";
import { useOcClientState } from "oc/hooks";
import useOcProperty from "oc/hooks/use-property";

const validator = new Validator();

interface MyHrLoginSSOFormProps {
  onLogin: (loginResult: any) => void;
}

export const MyHrLoginSSOForm: React.FC<MyHrLoginSSOFormProps> = ({
  onLogin,
}) => {
  const { t } = useTranslation();

  const schema = {
    username: {
      type: "string",
      min: 1,
      messages: {
        string: "rossz azonosító",
        stringMin: t("MY_HR_ERROR_MESSAGE_USERNAME_IS_REQUIRED"),
      },
    },
    password: {
      type: "string",
      min: 1,
      messages: {
        string: "Nem megfelelő jelszó!",
        stringMin: t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_REQUIRED"),
      },
    },
  };
  const check = validator.compile(schema);

  const [showDialog, setShowDialog] = useOcClientState<boolean>(
    "loginShowDialog",
    false,
  );

  const [username, setUsername] = useOcClientState<string>("loginUsername", "");
  const [usernameError, setUsernameError] = useOcClientState<boolean>(
    "loginUsernameError",
    false,
  );
  const [usernameErrorText, setUsernameErrorText] = useOcClientState<string>(
    "loginUsernameErrorText",
    "",
  );
  const [password, setPassword] = useOcClientState<string>("loginPassword", "");
  const [showPassword, setShowPassword] = useState(false);

  const [passwordError, setPasswordError] = useOcClientState<boolean>(
    "loginPasswordError",
    false,
  );
  const [passwordErrorText, setPasswordErrorText] = useOcClientState<string>(
    "loginPasswordErrorText",
    "",
  );

  const { data: passwordVisibilityData } = useOcProperty({
    resource: "system",
    key: "passwordVisibility",
  });

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const [ssoLogin, { loading, data: ssoLoginData, error: ssoLoginError }] = useLazyQuery(SSO_LOGIN, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (ssoLoginData?.ssoLogin) {
      onLogin(ssoLoginData.ssoLogin);
    }
  }, [ssoLoginData, onLogin]);

  useEffect(() => {
    if (ssoLoginError?.graphQLErrors) {
      ssoLoginError.graphQLErrors.forEach((item) => {
        if (item.message === "No user found!") {
          setUsernameErrorText(t("MY_HR_ERROR_MESSAGE_NO_USER_FOUND"));
          setUsernameError(true);
        }
        if (item.message === "Wrong username or password!") {
          setUsernameErrorText(
            t("MY_HR_ERROR_MESSAGE_WRONG_USERNAME_OR_PASSWORD"),
          );
          setUsernameError(true);
          setPasswordErrorText(
            t("MY_HR_ERROR_MESSAGE_WRONG_USERNAME_OR_PASSWORD"),
          );
          setPasswordError(true);
        }
      });
    }
  }, [ssoLoginError, t, setUsernameErrorText, setUsernameError, setPasswordErrorText, setPasswordError]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let isValidated = check({ username, password });

    if (isValidated instanceof Promise) {
      isValidated = await isValidated;
    }

    if (isValidated !== true) {
      isValidated.forEach((item: any) => {
        if (item.field === "username") {
          setUsernameErrorText(item.message);
          setUsernameError(true);
        }
        if (item.field === "password") {
          setPasswordErrorText(item.message);
          setPasswordError(true);
        }
      });
      return;
    }

    ssoLogin({ variables: { username, password } });
  };

  const handleClear = () => {
    setShowDialog(false);
    setUsername("");
    setUsernameError(false);
    setUsernameErrorText("");
    setPassword("");
    setPasswordError(false);
    setPasswordErrorText("");
  };

  useEffect(() => {
    return () => handleClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  return (
    <Fragment>
      <OcLoading show={loading} />
      <Box id="login-form" width="304px" padding="16px 48px">
        <form noValidate autoComplete="off" onSubmit={handleLogin}>
          <Box paddingTop="18px" paddingBottom="14px">
            <TextField
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              label={t("MY_HR_USERNAME")}
              variant="outlined"
              fullWidth
              error={usernameError}
              onFocus={() => {
                setUsernameErrorText("");
                setUsernameError(false);
              }}
              helperText={usernameError && usernameErrorText}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InlineIcon
                      style={{ height: "24px", width: "24px" }}
                      icon={accountIcon}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box paddingTop="18px" paddingBottom="14px">
            <TextField
              id="password"
              defaultValue={password}
              onChange={(e) => setPassword(e.target.value)}
              label={t("MY_HR_PASSWORD")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              fullWidth
              helperText={passwordErrorText}
              error={passwordError}
              onFocus={() => {
                setPasswordErrorText("");
                setPasswordError(false);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InlineIcon height="24px" icon={keyIcon} />
                    <Box width="8px" />
                    {passwordVisibilityData === "true" && (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        style={{
                          backgroundColor: "#F1F3F4",
                          height: "48px",
                          width: "48px",
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon
                            style={{ height: "24px", width: "24px" }}
                          />
                        ) : (
                          <VisibilityIcon
                            style={{ height: "24px", width: "24px" }}
                          />
                        )}
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box paddingTop="24px">
            <Button
              id="loginButton"
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disableElevation
            >
              {t("MY_HR_LOGIN")}
            </Button>
          </Box>
          <Box height="36px" />
          <MyHrHelpdeskContact />
        </form>
      </Box>
      <MyHrForgotPasswordDialogSSO
        showDialog={showDialog}
        onShowDialog={handleCloseDialog}
        smallScreen={false}
      />
    </Fragment>
  );
};

export default MyHrLoginSSOForm;
